import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthShell } from "@/components/shared/AuthShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export function VerifyPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { verifyAndLogin, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<
    "pending" | "success" | "error" | "usePassword" | "noAccount"
  >("pending");
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      toast.error(t("auth.verify.error"));
      setStatus("error");
      return;
    }
    if (isLoading || isAuthenticated) return;
    if (startedRef.current) return;
    startedRef.current = true;

    verifyAndLogin(token)
      .then(() => {
        if (mountedRef.current) {
          toast.success(t("auth.verify.successToast"));
          setStatus("success");
        }
      })
      .catch((err: { response?: { status?: number } }) => {
        if (mountedRef.current) {
          if (err?.response?.status === 403) {
            setStatus("usePassword");
          } else if (err?.response?.status === 404) {
            toast.error(t("auth.noAccountForEmail"));
            setStatus("noAccount");
          } else {
            toast.error(t("auth.verify.error"));
            setStatus("error");
          }
        }
      });
  }, [token, verifyAndLogin, isLoading, isAuthenticated]);

  const returnTo = searchParams.get("returnTo") ?? "/";

  if (!isLoading && isAuthenticated) {
    return <Navigate to={returnTo} replace />;
  }

  if (status === "success") {
    return <Navigate to={returnTo} replace />;
  }

  /* No cover half on this one: it is a magic-link waiting room that resolves
     to a redirect in a second or two, so AuthShell gives it the plain centred
     column rather than a navy half nobody has time to read. */
  if (status === "usePassword") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">{t("auth.verify.usePassword")}</p>
          <Button asChild>
            <Link to="/login">{t("auth.verify.usePasswordLink")}</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (status === "noAccount") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">{t("auth.noAccountForEmail")}</p>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/login">{t("pages.common.back")}</Link>
            </Button>
            <Button asChild>
              <Link to="/register">{t("auth.login.createNewAccount")}</Link>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (status === "error") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">{t("auth.verify.error")}</p>
          <Button asChild variant="outline">
            <Link to="/login">{t("pages.common.back")}</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="flex flex-col items-center gap-4 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        <p className="text-muted-foreground">{t("auth.verify.success")}</p>
      </div>
    </AuthShell>
  );
}
